import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { Form, message, Spin } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { nanoid } from '@ant-design/pro-components';
import dayjs from 'dayjs';

import * as Icon from 'base/ui/components/Icons';
import PageWrapper from 'pages/Components/PageWrapper';
import CustomButton from 'base/ui/components/Button';
import BoxContainer from 'base/ui/components/BoxContainer';
import { usePermission } from 'hooks/usePermission';
import { SYSTEM_TITLE } from 'utils/constants';
import { removeEmptyValues } from 'utils/transformHelper';
import WasteHandoverTable from './WasteHandoverTable';
import { wasteHandoverPlanPresenter, commonPresenter, commonMasterDataPresenter, commonMasterDataStore, commonStore } from 'stores/root_store';

const createWasteHandoverPlanDetail = () => {
  return observer(() => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();

    const { wasteHandoverPlanId } = useParams();
    const locationState = location.state as { dataDetail?: any } | null;

    console.log('wasteHandoverPlanIdddddđ', wasteHandoverPlanId);


    usePermission('CREATE');
    const pageTitle = t('page-title.create');
    document.title = pageTitle + ' - ' + SYSTEM_TITLE;

    const id = nanoid();
    const [editableKeys, setEditableRowKeys] = useState<React.Key[]>([id]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [wasteOptions, setWasteOptions] = useState<{ label: string; value: string }[]>([]);
    const [vendorOptions, setVendorOptions] = useState<{ label: string; value: string }[]>([]);
    const [storageOptions, setStorageOptions] = useState<{ label: string; value: string }[]>([]);

    const loadWasteOptions = async () => {
      try {
        const result = await commonPresenter.getDropdownProductWaste();
        if (result && result.length > 0) {
          const mappedOptions = result.map((item) => ({
            label: item.value || '',
            value: item.key || '',
          }));
          setWasteOptions(mappedOptions);
        }
      } catch (error) {
        console.error('Error loading waste options:', error);
      }
    };

    const loadVendorOptions = async () => {
      try {
        // Load catalog VendorType để lấy type
        await commonMasterDataPresenter.getCommonDataByKey(commonMasterDataStore, 'VendorType');
        const vendorTypes = commonMasterDataStore.commonRespone?.data || [];

        // Tìm type "WASTE" trong catalog VendorType
        const wasteType = vendorTypes.find((item) => item.value?.toUpperCase() === 'NCC_CT');
        const type = wasteType?.key || 'NCC_CT';

        // Gọi API với type
        await commonPresenter.getDropdownVendor(commonStore, { type });
        if (commonStore.commonRespone.isSuccess && commonStore.commonRespone.data.length > 0) {
          const mappedOptions = commonStore.commonRespone.data.map((item) => ({
            label: item.value || '',
            value: item.key || '',
          }));
          setVendorOptions(mappedOptions);
        }
      } catch (error) {
        console.error('Error loading vendor options:', error);
      }
    };

    const loadStorageOptions = async () => {
      try {
        // Load catalog StockType để lấy type
        await commonMasterDataPresenter.getCommonDataByKey(commonMasterDataStore, 'StockType');
        const stockTypes = commonMasterDataStore.commonRespone?.data || [];

        // Tìm type "WASTE" trong catalog StockType
        const wasteType = stockTypes.find((item) => item.value?.toUpperCase() === 'KCT');
        const type = wasteType?.key || 'KCT';

        // Gọi API với type
        await commonPresenter.getDropdownStock(commonStore, { type });
        if (commonStore.commonRespone.isSuccess && commonStore.commonRespone.data.length > 0) {
          const mappedOptions = commonStore.commonRespone.data.map((item) => ({
            label: item.value || '',
            value: item.key || '',
          }));
          setStorageOptions(mappedOptions);
        }
      } catch (error) {
        console.error('Error loading storage options:', error);
      }
    };

    const handleSubmit = async (values: any, isContinue?: boolean) => {
      setSaving(true);
      try {
        // Map form values to API format
        const items = values.wasteHandoverItems?.map((item: any) => {
          // Tách phần tên từ wasteName nếu có format "mã | tên"
          let productName = item.wasteName || '';
          if (productName && productName.includes('|')) {
            const parts = productName.split('|');
            productName = parts.length > 1 ? parts[1].trim() : productName;
          }

          return {
            // Format thành chuỗi local không kèm timezone để tránh bị trừ 7 giờ
            deliveryDate: item.deliveryScheduleTime
              ? dayjs(item.deliveryScheduleTime).format('YYYY-MM-DDTHH:mm:ss.SSS')
              : null,
            productCode: item.wasteCode || '',
            productName: productName,
            vendorNumber: item.vendorId || '',
            vendorName: vendorOptions.find((v) => v.value === item.vendorId)?.label || '',
            storageLocation: item.storageLocation || '',
            handoverDocumentNumber: item.deliveryNumber || '',
            note: item.note || '',
          };
        });

        const request = {
          items: items || [],
        };

        const response = await wasteHandoverPlanPresenter.createWasteHandoverPlan(request);

        if (response.isSuccess) {
          message.success(t('message.create-success'));
          if (isContinue) {
            form.resetFields();
            form.setFieldsValue({
              wasteHandoverItems: [{ wasteHandoverItemId: id }],
            });
            setEditableRowKeys([id]);
          } else {
            navigate('/WasteHandoverPlan');
          }
        } else {
          message.error(response.message || 'Có lỗi xảy ra');
        }
      } catch (error) {
        console.error('Error creating waste handover plan:', error);
        message.error('Có lỗi xảy ra khi tạo kế hoạch');
      } finally {
        setSaving(false);
      }
    };

    useEffect(() => {
      Promise.allSettled([loadWasteOptions(), loadVendorOptions(), loadStorageOptions()]).finally(() =>
        setLoading(false)
      );
    }, []);

    // chi tiết Kế hoạch bàn giao chất thải / phế liệu theo id
    const [dataDetail, setDataDetail] = useState<any>(locationState?.dataDetail || {})
    const getDetail = async () => {
      if (!wasteHandoverPlanId) return;
      setLoading(true);
      try {
        const response = await wasteHandoverPlanPresenter.getWasteHandoverPlanDetail(wasteHandoverPlanId);
        if (response.isSuccess && response.data) {
          const detail = response.data;
          setDataDetail(detail);

        } else {
          message.error(response.message || 'Không tải được dữ liệu.');
        }
      } catch (error) {
        console.error('Error loading detail:', error);
        message.error('Có lỗi xảy ra khi tải dữ liệu.');
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (wasteHandoverPlanId) {
        // Trường hợp copy 1 kế hoạch (đi kèm id trên URL)
        getDetail();
      }
    }, [wasteHandoverPlanId]);

    return (
      <PageWrapper
        breadcrumb={[
          {
            text: t('waste-handover-plan.title'),
            to: '/WasteHandoverPlan',
          },
          {
            text: pageTitle,
          },
        ]}
        extras={
          <>
            <CustomButton
              icon={<Icon.Download className="!fill-[#262626]" />}
              disabled={saving}
              loading={saving}
              onClick={() => {
                form
                  .validateFields()
                  .then((values: any) => {
                    handleSubmit(values);
                  })
                  .catch((errors) => console.log(errors));
              }}
            >
              {t('btn.save')}
            </CustomButton>
            <CustomButton
              type="primary"
              icon={<Icon.Download />}
              disabled={saving}
              loading={saving}
              onClick={() => {
                form
                  .validateFields()
                  .then((values: any) => {
                    handleSubmit(values, true);
                  })
                  .catch((errors) => console.log(errors));
              }}
            >
              {t('btn.save-continue-create')}
            </CustomButton>
          </>
        }
      >
        <BoxContainer fullHeight className="!bg-transparent !p-0">
          <Spin spinning={loading}>
            <Form
              autoComplete="off"
              initialValues={{
                wasteHandoverItems: [{ wasteHandoverItemId: id }],
              }}
              layout="vertical"
              form={form}
            >
              <WasteHandoverTable
                editableKeys={editableKeys}
                id={id}
                setEditableRowKeys={setEditableRowKeys}
                wasteOptions={wasteOptions}
                vendorOptions={vendorOptions}
                storageOptions={storageOptions}

                dataDetail={dataDetail}
              />
            </Form>
          </Spin>
        </BoxContainer>
      </PageWrapper>
    );
  });
};

export default createWasteHandoverPlanDetail;
